import type { BatchProcessDto } from "@fp/admin-api-client";
import {
  BatchProcessArea,
  BatchProcessItemOperation,
  BatchProcessItemStatus,
} from "@fp/admin-api-client";
import type { BadgeColors } from "@fp/ui/components/base/badges/badge-types";

export function getCompletedItemCount(batch: BatchProcessDto): number {
  return batch.succeededCount + batch.noActionCount + batch.failedCount;
}

export function getRunningPayoutApproval(
  batches: BatchProcessDto[],
): BatchProcessDto | null {
  return (
    batches.find(
      (batch) =>
        batch.area === BatchProcessArea.PayoutRequest &&
        batch.operation === BatchProcessItemOperation.Approve &&
        !batch.isComplete,
    ) ?? null
  );
}

export function getVisibleWidgetBatches(
  batches: BatchProcessDto[],
  presentedIds: ReadonlySet<string>,
  dismissedIds: ReadonlySet<string>,
): BatchProcessDto[] {
  return batches.filter(
    (batch) => presentedIds.has(batch.id) && !dismissedIds.has(batch.id),
  );
}

export function getUndecidedBatches(
  batches: BatchProcessDto[],
  presentedIds: ReadonlySet<string>,
  decidedIds: ReadonlySet<string>,
): BatchProcessDto[] {
  return batches.filter(
    (batch) => presentedIds.has(batch.id) && !decidedIds.has(batch.id),
  );
}

const stageStatuses = [
  BatchProcessItemStatus.Pending,
  BatchProcessItemStatus.InProcess,
] as const;

const outcomeStatuses = [
  BatchProcessItemStatus.Succeeded,
  BatchProcessItemStatus.NoAction,
  BatchProcessItemStatus.Failed,
] as const;

export interface BatchStatusCount {
  status: BatchProcessItemStatus;
  count: number;
}

export function getSummaryStatusCounts(batch: BatchProcessDto): BatchStatusCount[] {
  const counts: Record<BatchProcessItemStatus, number> = {
    [BatchProcessItemStatus.Pending]: batch.pendingCount,
    [BatchProcessItemStatus.InProcess]: batch.inProcessCount,
    [BatchProcessItemStatus.Deferred]: batch.deferredCount,
    [BatchProcessItemStatus.Succeeded]: batch.succeededCount,
    [BatchProcessItemStatus.NoAction]: batch.noActionCount,
    [BatchProcessItemStatus.Failed]: batch.failedCount,
  };

  const outcomes = outcomeStatuses.filter((status) => counts[status] > 0);

  const deferred =
    counts[BatchProcessItemStatus.Deferred] > 0
      ? [BatchProcessItemStatus.Deferred]
      : [];

  const stages = stageStatuses.filter(
    (status, index) =>
      counts[status] > 0 ||
      outcomes.length > 0 ||
      deferred.length > 0 ||
      stageStatuses.slice(index + 1).some((later) => counts[later] > 0),
  );

  return [...stages, ...deferred, ...outcomes].map((status) => ({
    status,
    count: counts[status],
  }));
}

export function getProgressPercentage(batch: BatchProcessDto): number {
  if (batch.totalCount <= 0) {
    return 0;
  }

  return Math.round((getCompletedItemCount(batch) / batch.totalCount) * 100);
}

interface BatchItemStatusMeta {
  label: string;
  color: BadgeColors;
}

export function isSettledStatus(status: BatchProcessItemStatus): boolean {
  return (
    status === BatchProcessItemStatus.Succeeded ||
    status === BatchProcessItemStatus.NoAction ||
    status === BatchProcessItemStatus.Failed
  );
}

const batchItemStatusMeta: Record<BatchProcessItemStatus, BatchItemStatusMeta> =
  {
    [BatchProcessItemStatus.Pending]: { label: "Pending", color: "gray" },
    [BatchProcessItemStatus.InProcess]: {
      label: "In process",
      color: "blue-light",
    },
    [BatchProcessItemStatus.Deferred]: { label: "Deferred", color: "warning" },
    [BatchProcessItemStatus.Succeeded]: {
      label: "Succeeded",
      color: "success",
    },
    [BatchProcessItemStatus.NoAction]: { label: "No action", color: "gray" },
    [BatchProcessItemStatus.Failed]: { label: "Failed", color: "error" },
  };

const statusLabelsByOperation: Partial<
  Record<
    BatchProcessItemOperation,
    Partial<Record<BatchProcessItemStatus, string>>
  >
> = {
  [BatchProcessItemOperation.Preview]: {
    [BatchProcessItemStatus.Succeeded]: "Payable",
  },
  [BatchProcessItemOperation.Sync]: {
    [BatchProcessItemStatus.Succeeded]: "Synced",
  },
};

export function getBatchItemStatusMeta(
  status: BatchProcessItemStatus,
  operation: BatchProcessItemOperation,
): BatchItemStatusMeta {
  const meta = batchItemStatusMeta[status];
  const label = statusLabelsByOperation[operation]?.[status];

  return label ? { ...meta, label } : meta;
}

const batchAreaLabels: Record<BatchProcessArea, string> = {
  [BatchProcessArea.Fsm]: "FSM",
  [BatchProcessArea.PayoutRequest]: "Payout request",
};

export function getBatchAreaLabel(area: BatchProcessArea): string {
  return batchAreaLabels[area] ?? area;
}

export function getOutcomeTypeLabel(outcomeType: string): string {
  if (outcomeType.includes(".")) {
    return outcomeType;
  }

  const spaced = outcomeType.replace(/([a-z0-9])([A-Z])/g, "$1 $2");

  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}
